import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import axios from "axios";
import Layout from "../components/layout/LegalLayout.jsx";
import config from "../config.js";
import { useSelector } from "react-redux";





const AddCaseForm = () => {
  const [cases, setCases] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [editingCase, setEditingCase] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);
  const { API_BASE_URL } = config;
  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);

  const { control, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: {
      id: "",
      client: "",
      type: "",
      status: "",
      assignedTo: "",
      description: "",
      priority: "",
      openDate: new Date().toISOString().split("T")[0],
      lastUpdated: new Date().toISOString().split("T")[0],
      closingDate: "",
    },
  });

  const caseTypes = [
    "Tax Audit",
    "Compliance Review",
    "Legal Consultation",
    "Contract Review",
    "Regulatory Filing",
    "Dispute Resolution",
  ];

  const statusOptions = [
    "In Progress",
    "Pending Review",
    "Waiting for Client",
    "Completed",
    "On Hold",
  ];

  const priorityOptions = ["High", "Medium", "Low"];

  const watchStatus = watch("status");

  useEffect(() => {
    if (watchStatus === "Completed" && !editingCase) {
      setValue("closingDate", new Date().toISOString().split("T")[0]);
    }
  }, [watchStatus]);

  const api = axios.create({
    baseURL: `${API_BASE_URL}`,
    headers: {
      "Content-Type": "application/json",
    },
  });

  const fetchCases = async () => {
    try {
      const response = await api.get(`${API_BASE_URL}/api/v1/legalcases/${selectedCompanyId}`);
      setCases(response.data.data || []);
    } catch (error) {
      showSnackbar(
        "Error fetching cases: " +
          (error.response?.data?.message || error.message),
        "error"
      );
    }
  };

  useEffect(() => {
    if (selectedCompanyId) {
      fetchCases();
    }
  }, [selectedCompanyId]);

  const onSubmit = async (data) => {
    try {
      const submissionData = {
        ...data,
        lastUpdated: new Date().toISOString(),
        kycId: selectedCompanyId,
        openDate: new Date(data.openDate).toISOString(),
        closingDate: data.closingDate
          ? new Date(data.closingDate).toISOString()
          : null,
      };

      if (editingCase) {
        await api.put(`/api/v1/legalcase/${editingCase._id}`, submissionData);
        showSnackbar("Case updated successfully");
      } else {
        await api.post(
          `/api/v1/legalcase/${selectedCompanyId}`,
          submissionData
        );
        showSnackbar("Case added successfully");
      }
      fetchCases();
      reset();
      setEditingCase(null);
    } catch (error) {
      showSnackbar(
        `Error ${editingCase ? "updating" : "adding"} case: ${
          error.response?.data?.message || error.message
        }`,
        "error"
      );
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/v1/legalcase/${caseToDelete._id}`);
      showSnackbar("Case deleted successfully");
      fetchCases();
      setOpenDeleteDialog(false);
      setCaseToDelete(null);
    } catch (error) {
      showSnackbar(
        `Error deleting case: ${
          error.response?.data?.message || error.message
        }`,
        "error"
      );
    }
  };

  const handleEdit = (caseItem) => {
    setEditingCase(caseItem);
    Object.keys(caseItem).forEach((key) => {
      if (key !== "_id") {
        setValue(key, caseItem[key]);
      }
    });
  };

  const handleDeleteClick = (caseItem) => {
    setCaseToDelete(caseItem);
    setOpenDeleteDialog(true);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const renderCasesTable = () => {
    if (!Array.isArray(cases)) {
      return (
        <Typography color="error">
          No cases available or error loading cases
        </Typography>
      );
    }

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Case ID</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Open Date</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell>Closing Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cases.map((caseItem) => (
              <TableRow key={caseItem._id}>
                <TableCell>{caseItem.id}</TableCell>
                <TableCell>{caseItem.client}</TableCell>
                <TableCell>{caseItem.type}</TableCell>
                <TableCell>
                  <Chip
                    label={caseItem.status}
                    color={
                      caseItem.status === "In Progress"
                        ? "primary"
                        : caseItem.status === "Pending Review"
                        ? "warning"
                        : "default"
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={caseItem.priority}
                    color={
                      caseItem.priority === "High"
                        ? "error"
                        : caseItem.priority === "Medium"
                        ? "warning"
                        : "success"
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>{caseItem.assignedTo}</TableCell>
                <TableCell>
                  {new Date(caseItem.openDate).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {new Date(caseItem.lastUpdated).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {caseItem.closingDate
                    ? new Date(caseItem.closingDate).toLocaleDateString()
                    : "N/A"}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(caseItem)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(caseItem)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Layout>
      <Container maxWidth="md">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom>
              {editingCase ? "Edit Case" : "Add New Case"}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Enter the details for the legal case
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{ mt: 4 }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="id"
                    control={control}
                    rules={{ required: "Case ID is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Case ID"
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                        placeholder="AC-001"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="client"
                    control={control}
                    rules={{ required: "Client name is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Client Name"
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="type"
                    control={control}
                    rules={{ required: "Case type is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        select
                        label="Case Type"
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      >
                        {caseTypes.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="status"
                    control={control}
                    rules={{ required: "Status is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        select
                        label="Status"
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      >
                        {statusOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="priority"
                    control={control}
                    rules={{ required: "Priority is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        select
                        label="Priority"
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      >
                        {priorityOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="assignedTo"
                    control={control}
                    rules={{ required: "Assigned person is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Assigned To"
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="openDate"
                    control={control}
                    rules={{ required: "Open date is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        type="date"
                        label="Open Date"
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="lastUpdated"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="date"
                        label="Last Updated"
                        fullWidth
                        disabled
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="closingDate"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="date"
                        label="Closing Date"
                        fullWidth
                        disabled={watchStatus !== "Completed"}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    rules={{ required: "Description is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Description"
                        multiline
                        rows={4}
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    {editingCase ? "Update Case" : "Add Case"}
                  </Button>
                  {editingCase && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="large"
                      fullWidth
                      sx={{ mt: 2 }}
                      onClick={() => {
                        setEditingCase(null);
                        reset();
                      }}
                    >
                      Cancel Edit
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Paper>

          <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
            <Typography variant="h5" gutterBottom>
              Active Cases
            </Typography>
            {renderCasesTable()}
          </Paper>
        </Box>

        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            Are you sure you want to delete case {caseToDelete?.id}?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={snackbarSeverity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
};

export default AddCaseForm;
